import React, { useCallback, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useSelector } from 'react-redux';
import { Edit, Engine } from '@picsio/icons';
import SvgCollapseArrowDown from '@picsio/icons/dist/CollapseArrowDown';
import {
  IconButton, Menu, MenuItem, MenuItemIcon, MenuItemText,
} from '@picsio/ui';

import { selectAdminRoles } from '../../../../store/selectors/teammates';
import Logger from '../../../../services/Logger';
import localization from '../../../../shared/strings';
import { navigate } from '../../../../helpers/history';
import { isHaveTeammatePermission } from '../../../../store/helpers/user';
import { showDialog } from '../../../dialog';
import Tag from '../../../Tag/Tag';
import styles from './styles.module.scss';

const PromptIcon = ({
  className, promptValue, promptKey, disabled,
}) => {
  const user = useSelector((state) => state.user);
  const adminRoles = useSelector(selectAdminRoles);

  const [isPromptMenuOpen, setIsPromptMenuOpen] = useState(false);

  const targetRef = useRef(null);

  const onShowActiveAIPrompt = useCallback(() => {
    Logger.log('User', 'InfoPanelSeeActiveAIPromptClicked', { initiation_location: promptKey });
    showDialog({
      title: localization.PROMPT_ICON.DIALOG.TITLE,
      textarea: {
        readOnly: true,
        value: promptValue ?? '',
        useResizing: false,
        onResize: null,
        className: styles.promptTextArea,
        label: localization.TEAMMATES.editPromptModal.inputLabel,
        rows: 8,
      },
      buttonOkColor: 'secondary',
      textBtnOk: localization.DIALOGS.btnOk,
      textBtnCancel: null,
      onCancel: () => Logger.log('User', 'InfoPanelActiveAIPromptModalClosed', { initiation_location: promptKey }),
      onOk: () => Logger.log('User', 'InfoPanelActiveAIPromptModalClosed', { initiation_location: promptKey }),
    });
    Logger.log('User', 'InfoPanelActiveAIPromptModalShown', { initiation_location: promptKey });

    setIsPromptMenuOpen(false);
  }, [promptKey, promptValue]);

  const onEditAIPrompt = useCallback(() => {
    Logger.log('User', 'InfoPanelEditAIPromptClicked', { initiation_location: promptKey });
    setIsPromptMenuOpen(false);

    if (isHaveTeammatePermission('manageTeam', user)) {
      navigate('/teammates?tab=ai', { shouldOpenEditPromptModal: true, promptKey, promptValue });

      return;
    }

    const adminRolesEmails = adminRoles.map((role) => (
      <Tag
        key={role._id}
        type="user"
        avatar={role.avatar}
        text={role.email}
      />
    ));

    showDialog({
      title: localization.PROMPT_ICON.DIALOG.EDIT_TITLE,
      text: localization.PROMPT_ICON.DIALOG.text(adminRolesEmails),
      textBtnCancel: null,
      textBtnOk: localization.DIALOGS.BILLING_ACCESS_REQUIRED.textBtnOk,
      buttonOkColor: 'secondary',
      onCancel: () => Logger.log('User', 'EditAIPromptAccessDeniedModalClosed'),
      onOk: () => Logger.log('User', 'EditAIPromptAccessDeniedModalClosed'),
    });
    Logger.log('User', 'EditAIPromptAccessDeniedModalShown');
  }, [adminRoles, promptKey, promptValue, user]);

  const menuItems = [
    {
      id: localization.PROMPT_ICON.MENU_ITEMS.ACTIVE_AI_PROMPT,
      text: localization.PROMPT_ICON.MENU_ITEMS.ACTIVE_AI_PROMPT,
      icon: <Engine data-testid="active-ai-prompt" />,
      onClick: onShowActiveAIPrompt,
    },
    {
      id: localization.PROMPT_ICON.MENU_ITEMS.EDIT_AI_PROMPT,
      text: localization.PROMPT_ICON.MENU_ITEMS.EDIT_AI_PROMPT,
      icon: <Edit data-testid="edit-ai-prompt" />,
      onClick: onEditAIPrompt,
    },
  ];

  const onToggleMenu = useCallback(() => {
    setIsPromptMenuOpen(!isPromptMenuOpen);
  }, [isPromptMenuOpen]);

  return (
    <div ref={targetRef} className={cn(className, styles.prompt)}>
      <IconButton
        disabled={disabled}
        size="lg"
        onClick={onToggleMenu}
      >
        <SvgCollapseArrowDown className={cn(styles.promptIcon, { [styles.activeIcon]: isPromptMenuOpen })} />
      </IconButton>
      <Menu
        outsideClickListener
        placement="bottom"
        className={styles.promptMenu}
        isOpen={isPromptMenuOpen}
        target={targetRef}
        onClose={onToggleMenu}
      >
        {menuItems.map((menuItem) => (
          <MenuItem className="menuItemDefault centered" disabled={disabled} onClick={menuItem.onClick}>
            <MenuItemIcon size="xl">{menuItem.icon}</MenuItemIcon>
            <MenuItemText primary={menuItem.text} />
          </MenuItem>
        ))}
      </Menu>
    </div>
  );
};

PromptIcon.defaultProps = {
  className: '',
  disabled: false,
  promptValue: '',
  promptKey: '',
};

PromptIcon.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  promptValue: PropTypes.string,
  promptKey: PropTypes.string,
};

export default PromptIcon;
